// Money owed and credit in a Squeegee backup (docs/plans/moving-in-with-checks/PLAN.md v3, Liam's answers
// 2 Oct 2026). Pure: the import screen passes in the backup's objects (squeegee-backup.ts readBackupData)
// and gets back, per Squeegee customer, the figure Squeegee shows, what would come across, and every
// question the owner must answer before Add. Nothing here is sent or saved yet (release step 1).
//
// Squeegee's own rules, read from its web app (sqgee.com 1.56.83, saved bundles, 2 Oct 2026):
//   A customer's balance is the plain sum of `amount` over their `transactions`, `_deleted` ones left out
//   and nothing else: no filter on status, type or cancellation (getCustomerBalance, getBalance:
//   `Number(amount) || 0`, rounded to the penny at the end). Positive is owed, negative is credit.
//   Cancelling (createVoid, createReversal) adds a record of minus the amount and links the two by
//   `voidedId` both ways, so a live pair adds up to nothing.
//   A GoCardless payment (`auto.go-cardless`) is negative and `pending` from the moment it is made, so it
//   already lowers the balance. A failed one stays counted unless something cancels it.
//   Squeegee's "Fix incorrectly voided and duplicate void gocardless payments" (bundle app-bc1f2f8e), run
//   by hand: a `failed` payment whose paymentHistory says confirmed or paid_out is put back to complete and
//   its cancellation deleted; one cancellation pointing at a payment that points elsewhere is re-linked;
//   extra cancellations of one payment are deleted. Nothing shows it runs by itself, so a backup can hold
//   any of these.
//
// What comes across for each customer is Squeegee's figure, except where the owner answers otherwise.
// The questions (PLAN section 4), each with the answers allowed and the one picked to start with (null:
// none, so Add stays off until the owner picks):
//   1 stopped     stopped or archived in Squeegee with money: lapsed (Liam Q3) | leave_out
//   2 failed      a failed payment Squeegee still counts, nothing cancelling it: paid | add_back (Liam Q5: no pick)
//   3 contradiction
//                 failed but its history says paid out: paid (Squeegee's own fix) | add_back;
//                 a cancellation missing, deleted, doubled, pointing elsewhere or of a different amount:
//                 as_squeegee | cancelled | stands. Doubled starts on cancelled and pointing elsewhere on
//                 as_squeegee (what Squeegee's fix leaves); the rest start on none.
//   4 unreadable  an unknown type or subtype, an amount that is not a number (Squeegee counts 0), not the
//                 round's currency, or part of a penny: as_squeegee | leave_out | pence:N (the owner types it)
//   5 two_houses  one Squeegee customer at two houses (two Roundly customers): house:N, starting on the first
//                 (Liam Q7)
//   6 newer       already brought over from an older backup, and the figure has moved: accept | keep
//                 (Liam Q10: nothing added twice; the difference is offered)
// A pair that cancels properly is "net £0" and never asked. Money for a customer deleted in Squeegee, or
// not in the file, is listed and never brought (7).
//
// settleMoney applies the answers: the opening figure for each account, what each direct debit still
// collecting already took off it (opening_effect_pence), and the totals for the check screen, owed and
// credit apart (never netted).
import { dayIn } from './round-config'

import { addressOf, customerJobs, squeegeeRefs } from './squeegee-backup'

type Obj = Record<string, unknown>
const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v)
const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : typeof v === 'number' && Number.isFinite(v) ? String(v) : '')
const gone = (o: Obj): boolean => o._deleted === true

export type MoneyKind = 'stopped' | 'failed' | 'contradiction' | 'unreadable' | 'two_houses' | 'newer'
export type LineKind = 'invoice' | 'payment' | 'refund' | 'credit_note' | 'adjustment' | 'collection' | 'note' | 'other'

export interface MoneyQuestion {
  // Stable for the same file: the kind and the Squeegee _id it is about.
  id: string
  kind: MoneyKind
  // The plan's exception number (section 4).
  number: 1 | 2 | 3 | 4 | 5 | 6
  ref: string
  name: string
  // In the owner's words.
  says: string
  // The money in question, in pence (the payment's own amount; for "stopped", the customer's figure).
  pence: number
  options: string[]
  // The owner may type the amount ("pence:1200").
  typed: boolean
  default: string | null
  // Squeegee records the question is about: their history lines are never left out.
  records: string[]
  // For the payment questions (2 and 3): for each answer, whether each record counts (1) or not (0).
  counts?: Record<string, Record<string, 0 | 1>>
}

export interface MoneyLine { ext_id: string; kind: LineKind; on_date: string; text: string; amount_pence: number | null; mandatory: boolean }

export interface MoneyCollection {
  ext_id: string
  provider: string
  provider_payment_id: string | null
  // What is being collected, in pence (positive).
  amount_pence: number
  on_date: string
  squeegee_status: string
}

export interface MoneyAccount {
  squeegee_ref: string
  squeegee_id: string
  name: string
  // Where the import puts this customer, one Roundly customer each, in the import's order.
  houses: string[]
  stopped: boolean
  // Their phone and email, for a stopped customer the owner brings in as Lapsed (the import's rows leave them out).
  phone: string
  email: string
  // Squeegee's own figure: positive owed, negative credit.
  squeegee_pence: number
  // Every live money record and what Squeegee counts it as, in pounds and NOT rounded (Number(amount) || 0):
  // Squeegee rounds only the customer's total, so settleMoney does the same.
  records: Record<string, number>
  collections: MoneyCollection[]
  // Every direct debit Squeegee holds for them and where it stands now, so a newer backup's correction also settles
  // the debit Roundly has still collecting (Astra, steps 5-6 finding 2): never added back twice.
  debits: { ext_id: string; state: 'collecting' | 'failed' | 'collected' }[]
  // The history kept (all notes up to 200, 12 months of money up to 100, and every line behind a
  // question or a collection whatever its age), newest first; lines_left: the others, counted.
  lines: MoneyLine[]
  lines_left: number
  questions: string[]
  // Already brought over from an older backup: what Roundly took from Squeegee for them so far, and the reference
  // Roundly holds for the account (Squeegee's may have changed since; matched by Squeegee id first).
  already: { accepted_pence: number; squeegee_ref: string } | null
  // Its reference is already Roundly's for a different Squeegee customer (Astra, steps 5-6 short check finding 4):
  // never brought, never corrected, named.
  conflict: boolean
}

export interface NotBrought { squeegee_id: string; name: string; reason: 'deleted' | 'not_in_file'; pence: number }

export interface SqueegeeMoney {
  // When the backup was made (UTC), when known.
  as_of: string | null
  currency: string
  accounts: MoneyAccount[]
  questions: MoneyQuestion[]
  not_brought: NotBrought[]
  // What Squeegee says, live customers only, owed and credit apart.
  squeegee: { owed_pence: number; owed_customers: number; credit_pence: number; credit_customers: number }
}

export interface MoneyOptions {
  asOf?: string | null
  // The round's currency (Roundly: GBP, USD or CAD by country).
  currency?: string
  // Accounts already on the round from an earlier import, by Squeegee ref or id.
  known?: { squeegee_ref: string; squeegee_id?: string | null; accepted_pence: number }[]
}

export const NOTES_KEPT = 200
export const MONEY_LINES_KEPT = 100
export const COLLECTIONS_MAX = 50

// ---- Squeegee's types ---------------------------------------------------------------------------------------
// transactionType: 0 Invoice, 1 Payment, 2 DiscountDeprecated, 3 Adjustment, 4 Expense, 5 AutomaticPayment,
// 6 Summary, 7 Transfer, 8 Unreconciled. Subtypes as Squeegee's type definitions list them.
const SUBTYPES = new Set([
  'invoice.standard', 'invoice.imported', 'invoice.credit-note', 'invoice.write-off', 'invoice.other', 'invoice.tip',
  'payment.cash', 'payment.cheque', 'payment.bank-transfer', 'payment.card', 'payment.other', 'payment.imported', 'payment.stripe',
  'payment.square', 'payment.refund', 'payment.cis', 'payment.vat',
  'refund.cash', 'refund.cheque', 'refund.bank-transfer', 'refund.card', 'refund.other', 'refund.imported', 'refund.stripe', 'refund.direct-debit',
  'discount.voucher', 'discount.other',
  'adjustment.closing-write-off', 'adjustment.initial-balance', 'adjustment.initial-balance-imported', 'adjustment.write-off-imported',
  'adjustment.write-off', 'adjustment.other', 'adjustment.tip', 'adjustment.void',
  'expense.standard', 'expense.squeegee', 'expense.imported', 'expense.other', 'expense.refund', 'expense.vat',
  'auto.go-cardless', 'auto.stripe', 'auto.other',
  'summary.aggregate', 'transfer.in', 'transfer.out', 'unreconciled.debit', 'unreconciled.credit',
])
const knownSubtype = (s: string): boolean => SUBTYPES.has(s) || /^payment\.tip\.[a-z-]+$/.test(s) || /^payment\.custom-[\w-]+$/.test(s)
const INVOICE = 0
const PAYMENT = 1
const AUTOMATIC = 5
// Still being collected: GoCardless's pending, and Squeegee's own "being sent" states.
const COLLECTING = new Set(['pending', 'awaitingApiHandling', 'apiHandlingInProgress'])
// Never arrived.
const FAILED = new Set(['failed', 'canceled', 'apiHandlingFailed'])

// ---- words --------------------------------------------------------------------------------------------------
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const dayWords = (iso: string): string => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${Number(iso.slice(8))} ${MON[Number(iso.slice(5, 7)) - 1]} ${iso.slice(0, 4)}` : 'an unknown date')
export const pounds = (pence: number): string => `£${(Math.abs(pence) / 100).toFixed(2)}`
const owesWords = (pence: number): string => (pence > 0 ? `owes ${pounds(pence)}` : pence < 0 ? `has ${pounds(pence)} credit` : 'owes nothing')
const figureWords = (pence: number): string => (pence > 0 ? `${pounds(pence)} owed` : pence < 0 ? `${pounds(pence)} credit` : pounds(0))

function label(r: Obj): string {
  const sub = text(r.transactionSubType)
  const amount = Number(r.amount) || 0
  if (/^CANCELLED:/.test(text(r.description))) return 'Cancellation'
  if (/^REVERSAL:/.test(text(r.description))) return 'Reversal'
  if (sub === 'auto.go-cardless') return 'Direct debit'
  if (sub.startsWith('auto.')) return 'Automatic payment'
  if (sub === 'invoice.credit-note') return 'Credit note'
  if (sub === 'invoice.write-off' || sub.endsWith('write-off') || sub.endsWith('write-off-imported')) return 'Written off'
  if (sub.startsWith('invoice.')) return 'Invoice'
  if (sub.startsWith('refund.') || sub === 'payment.refund') return 'Refund'
  if (sub.startsWith('adjustment.initial-balance')) return 'Opening balance'
  if (sub.startsWith('discount.')) return 'Discount'
  if (sub === 'summary.aggregate') return 'Earlier balance'
  if (sub.startsWith('payment.tip') || sub.endsWith('.tip')) return 'Tip'
  if (num(r.transactionType) === PAYMENT || sub.startsWith('payment.')) {
    if (amount > 0) return 'Payment reversal'
    return { 'payment.cash': 'Cash', 'payment.cheque': 'Cheque', 'payment.bank-transfer': 'Bank transfer', 'payment.card': 'Card' }[sub] ?? 'Payment'
  }
  if (sub.startsWith('adjustment.')) return 'Adjustment'
  return 'Money record'
}

function lineKind(r: Obj): LineKind {
  const sub = text(r.transactionSubType)
  const type = num(r.transactionType)
  if (type === AUTOMATIC && COLLECTING.has(text(r.status))) return 'collection'
  if (sub === 'invoice.credit-note') return 'credit_note'
  if (sub.startsWith('refund.') || sub === 'payment.refund') return 'refund'
  if (type === INVOICE) return 'invoice'
  if (type === PAYMENT || type === AUTOMATIC) return 'payment'
  if (type === 3 || type === 2) return 'adjustment'
  return 'other'
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)
const dateOf = (r: Obj): string => [r.date, r.createdDate].map(text).find((v) => /^\d{4}-\d{2}-\d{2}/.test(v))?.slice(0, 10) ?? ''
// Squeegee's count of a record, to the penny.
const squeegeePence = (r: Obj): number => Math.round((Number(r.amount) || 0) * 100)
// Squeegee's figure for a customer: the plain sum, rounded at the end (to2dp).
function squeegeeFigure(records: Obj[]): number {
  const sum = records.reduce((s, r) => s + (Number(r.amount) || 0), 0)
  return Math.round((sum ? Math.round(100 * (sum + Number.EPSILON)) / 100 : 0) * 100)
}
const currencyOf = (r: Obj): string[] =>
  [r.currency, isObj(r.paymentDetails) ? r.paymentDetails.currency : undefined, isObj(r.invoice) ? r.invoice.currency : undefined].map(text).filter(Boolean)

// Why a record cannot be read as it stands, in the owner's words, or null.
function unreadable(r: Obj, currency: string): string | null {
  const type = num(r.transactionType)
  if (type === null || !Number.isInteger(type) || type < 0 || type > 8) return 'a kind of record Roundly does not know'
  const sub = r.transactionSubType
  if (sub !== undefined && sub !== null && (typeof sub !== 'string' || !knownSubtype(sub))) return 'a kind of record Roundly does not know'
  if (typeof r.amount !== 'number' || !Number.isFinite(r.amount)) return 'its amount is not a number'
  if (currencyOf(r).some((c) => c.toUpperCase() !== currency)) return `it is not in ${currency}`
  if (Math.abs(r.amount * 100 - Math.round(r.amount * 100)) > 1e-6) return 'its amount has part of a penny'
  return null
}

// ---- the reader ---------------------------------------------------------------------------------------------
export function squeegeeMoney(data: unknown, opts: MoneyOptions = {}): SqueegeeMoney {
  const currency = (opts.currency ?? 'GBP').toUpperCase()
  const objects = Array.isArray(data) ? data.filter(isObj) : []
  const refs = squeegeeRefs(objects)
  const byId = new Map<string, Obj>()
  for (const o of objects) if (text(o._id) && !byId.has(text(o._id))) byId.set(text(o._id), o)
  const customers = objects.filter((o) => o.resourceType === 'customers' && text(o._id))
  const customerById = new Map(customers.map((c) => [text(c._id), c]))
  const transactions = objects.filter((o) => o.resourceType === 'transactions' && text(o._id))
  // Live records pointing at each id by voidedId.
  const pointedBy = new Map<string, Obj[]>()
  for (const t of transactions) if (!gone(t) && text(t.voidedId)) pointedBy.set(text(t.voidedId), [...(pointedBy.get(text(t.voidedId)) ?? []), t])
  const mine = new Map<string, Obj[]>()
  for (const t of transactions) if (!gone(t) && text(t.customerId)) mine.set(text(t.customerId), [...(mine.get(text(t.customerId)) ?? []), t])
  const notes = new Map<string, Obj[]>()
  for (const n of objects) if (n.resourceType === 'notes' && !gone(n) && text(n.relatedId)) notes.set(text(n.relatedId), [...(notes.get(text(n.relatedId)) ?? []), n])

  const asOf = opts.asOf ?? null
  const asOfDay = asOf ? asOf.slice(0, 10) : null
  const yearBack = asOfDay ? `${Number(asOfDay.slice(0, 4)) - 1}${asOfDay.slice(4)}` : null
  const known = opts.known ?? []
  const questions: MoneyQuestion[] = []
  const accounts: MoneyAccount[] = []
  const notBrought: NotBrought[] = []

  // Money for someone who is not a live customer in this file: listed, never brought (7).
  for (const [cid, records] of mine) {
    const c = customerById.get(cid)
    if (c && !gone(c)) continue
    notBrought.push({ squeegee_id: cid, name: c ? text(c.name) || addressOf(c.address) : '', reason: c ? 'deleted' : 'not_in_file', pence: squeegeeFigure(records) })
  }

  // A Squeegee _id written twice: its money (found by that _id) is counted once, on the first customer that has
  // it; the others come in as customers with no account.
  const counted = new Set<string>()
  for (const c of customers) {
    if (gone(c)) continue
    const id = text(c._id)
    if (counted.has(id)) continue
    counted.add(id)
    const ref = refs.get(c) ?? id
    const name = text(c.name) || addressOf(c.address)
    const records = mine.get(id) ?? []
    const ask = (q: Omit<MoneyQuestion, 'ref' | 'name'>): void => {
      questions.push({ ...q, ref, name })
      account.questions.push(q.id)
    }
    // The houses, as the import makes them: each live job's address (the customer's own when it has none),
    // the same address once; a customer with no live job, their own address.
    const norm = (v: string): string => v.toLowerCase().replace(/\s*,\s*/g, ', ').replace(/\s+/g, ' ').trim()
    const houses: string[] = []
    const { live } = customerJobs(c)
    for (const j of live.length ? live : [null]) {
      const at = (j && addressOf(j.location)) || addressOf(c.address)
      if (!houses.some((h) => norm(h) === norm(at))) houses.push(at)
    }
    const account: MoneyAccount = {
      squeegee_ref: ref,
      squeegee_id: id,
      name,
      houses,
      stopped: c.state === 'inactive' || c._archived === true,
      phone: text(c.telephoneNumber),
      email: text(c.email),
      squeegee_pence: squeegeeFigure(records),
      records: Object.fromEntries(records.map((r) => [text(r._id), Number(r.amount) || 0])),
      collections: [],
      debits: [],
      lines: [],
      lines_left: 0,
      questions: [],
      already: null,
      conflict: false,
    }
    const mandatory = new Set<string>()
    // Records in a pair that cancels properly (net £0).
    const paired = new Set<string>()
    const whenOf = (r: Obj): string => dateOf(r)
    const paidOut = (x: Obj): boolean => FAILED.has(text(x.status)) && isObj(x.paymentDetails) && Array.isArray(x.paymentDetails.paymentHistory)
      && x.paymentDetails.paymentHistory.some((e) => isObj(e) && (e.action === 'confirmed' || e.action === 'paid_out'))
    // Failed, but its history says paid out (3): Squeegee's own fix counts it paid and deletes its
    // cancellations, so that is the answer to start with.
    const askPaidOut = (original: Obj, group: Obj[]): void => {
      for (const x of group) mandatory.add(text(x._id))
      const amount = -squeegeePence(original)
      const all = Object.fromEntries(group.map((x) => [text(x._id), 0 as 0 | 1]))
      ask({
        id: `contradiction:${text(original._id)}`, kind: 'contradiction', number: 3, pence: amount,
        says: `${pounds(amount)} ${label(original).toLowerCase()} on ${dayWords(whenOf(original))} is marked failed, but its history says it was paid out. Paid, or add it back?`,
        options: ['paid', 'add_back'], typed: false, default: 'paid', records: group.map((x) => text(x._id)),
        counts: { paid: { ...all, [text(original._id)]: 1 }, add_back: all },
      })
    }

    // Cancellations. A cancellation is what createVoid/createReversal writes: a record pointing at another by
    // voidedId, written after it ("CANCELLED: " or "REVERSAL: " first in its description, or else created later),
    // whatever else links to it. Each record is looked at with ONLY the cancellations that point at it, so a
    // payment whose voidedId wrongly names some unrelated record never takes that record with it: Squeegee's own
    // repair (app-bc1f2f8e) finds cancellations the same way (voidedId = the payment) and leaves anything else.
    const own = new Map(records.map((r) => [text(r._id), r]))
    const createdOf = (x: Obj): string => text(x.createdDate)
    const isCancellation = (x: Obj): boolean => {
      const target = text(x.voidedId)
      if (!target) return false
      if (/^(CANCELLED|REVERSAL):/.test(text(x.description))) return true
      const t = own.get(target)
      if (!t || /^(CANCELLED|REVERSAL):/.test(text(t.description))) return false
      // Neither says so. A cancellation is minus what it cancels, and nothing that says it is a cancellation
      // points at it; then the later one is the cancellation (by id when they were made at the same moment).
      if (squeegeePence(x) !== -squeegeePence(t)) return false
      if ((pointedBy.get(text(x._id)) ?? []).some((p) => /^(CANCELLED|REVERSAL):/.test(text(p.description)))) return false
      return createdOf(x) > createdOf(t) || (createdOf(x) === createdOf(t) && text(x._id) > text(t._id))
    }
    // Records already in a question or a pair that cancels properly (net £0).
    const grouped = new Set<string>()
    for (const r of records) {
      const rid = text(r._id)
      if (isCancellation(r)) continue
      const cancels = (pointedBy.get(rid) ?? []).filter((x) => own.has(text(x._id)) && isCancellation(x))
      if (!text(r.voidedId) && !cancels.length) continue
      const group = [r, ...cancels]
      for (const x of group) grouped.add(text(x._id))
      const all = (v: 0 | 1): Record<string, 0 | 1> => Object.fromEntries(group.map((x) => [text(x._id), v]))
      if (paidOut(r)) {
        askPaidOut(r, group)
        continue
      }
      const amount = squeegeePence(r)
      const matching = cancels.every((x) => squeegeePence(x) === -amount)
      if (cancels.length === 1 && matching && text(r.voidedId) === text(cancels[0]._id)) {
        // Net £0: shown in the history, never asked.
        for (const x of group) paired.add(text(x._id))
        continue
      }
      for (const x of group) mandatory.add(text(x._id))
      const target = text(r.voidedId) ? byId.get(text(r.voidedId)) : undefined
      const why = cancels.length > 1 ? `it is cancelled ${cancels.length} times${matching ? '' : ', not all for the same amount'}`
        : cancels.length === 1 && !matching ? 'the cancellation is for a different amount'
        : cancels.length === 1 ? 'its cancellation points at another record'
        : target && gone(target) ? 'its cancellation is deleted'
        : target ? 'it is marked cancelled by a record that is not its cancellation'
        : 'its cancellation is missing'
      // Where the answer is clear from Squeegee's own repair: cancelled several times over by the same amount,
      // one cancellation kept (net £0); one cancellation pointing back with the payment pointing elsewhere,
      // re-linked (no money moves). Anything else is the owner's to say.
      const pick = cancels.length > 1 && matching ? 'cancelled' : cancels.length === 1 && matching ? 'as_squeegee' : null
      const squeegeeCounts = group.reduce((t, x) => t + squeegeePence(x), 0)
      ask({
        id: `contradiction:${rid}`, kind: 'contradiction', number: 3, pence: Math.abs(amount),
        says: `${pounds(amount)} ${label(r).toLowerCase()} on ${dayWords(whenOf(r))}: ${why}. Squeegee counts ${pounds(squeegeeCounts)}${squeegeeCounts < 0 ? ' off' : ''}. Count it as Squeegee does, as cancelled, or as never cancelled?`,
        options: cancels.length ? ['as_squeegee', 'cancelled', 'stands'] : ['as_squeegee', 'cancelled'],
        typed: false,
        default: pick,
        records: group.map((x) => text(x._id)),
        counts: {
          as_squeegee: all(1),
          cancelled: all(0),
          ...(cancels.length ? { stands: { ...all(0), [rid]: 1 } } : {}),
        },
      })
    }
    // A cancellation whose record is not here, or is deleted: Squeegee counts it on its own.
    for (const x of records) {
      const xid = text(x._id)
      if (!isCancellation(x) || own.has(text(x.voidedId)) || grouped.has(xid)) continue
      grouped.add(xid)
      mandatory.add(xid)
      const amount = squeegeePence(x)
      ask({
        id: `contradiction:${xid}`, kind: 'contradiction', number: 3, pence: Math.abs(amount),
        says: `${pounds(amount)} ${label(x).toLowerCase()} on ${dayWords(whenOf(x))}: the payment it cancels is deleted or missing. Squeegee counts ${pounds(amount)}${amount < 0 ? ' off' : ''}. Count it as Squeegee does, or as cancelled?`,
        options: ['as_squeegee', 'cancelled'], typed: false, default: null, records: [xid],
        counts: { as_squeegee: { [xid]: 1 }, cancelled: { [xid]: 0 } },
      })
    }

    for (const r of records) {
      const rid = text(r._id)
      // A failed payment nothing cancels: Squeegee still counts it (2).
      const type = num(r.transactionType)
      if ((type === PAYMENT || type === AUTOMATIC) && FAILED.has(text(r.status)) && squeegeePence(r) < 0 && !grouped.has(rid) && paidOut(r)) askPaidOut(r, [r])
      else if ((type === PAYMENT || type === AUTOMATIC) && FAILED.has(text(r.status)) && squeegeePence(r) < 0 && !grouped.has(rid)) {
        mandatory.add(rid)
        ask({
          id: `failed:${rid}`, kind: 'failed', number: 2, pence: -squeegeePence(r),
          says: `${pounds(squeegeePence(r))} ${label(r).toLowerCase()} ${text(r.status) === 'canceled' ? 'was cancelled' : 'failed'} on ${dayWords(whenOf(r))}, and Squeegee still counts it as paid. Paid, or add it back?`,
          options: ['paid', 'add_back'], typed: false, default: null, records: [rid],
          counts: { paid: { [rid]: 1 }, add_back: { [rid]: 0 } },
        })
      }
      if (type === AUTOMATIC) account.debits.push({ ext_id: rid, state: COLLECTING.has(text(r.status)) ? 'collecting' : FAILED.has(text(r.status)) ? 'failed' : 'collected' })
      // Can't be read (4).
      // A direct debit still collecting can only lower what is owed; one that adds to it cannot be read as one.
      const collecting = type === AUTOMATIC && COLLECTING.has(text(r.status))
      const why = unreadable(r, currency) ?? (collecting && squeegeePence(r) > 0 ? 'a direct debit still collecting that adds to what is owed' : null)
      if (why) {
        mandatory.add(rid)
        ask({
          id: `unreadable:${rid}`, kind: 'unreadable', number: 4, pence: Math.abs(squeegeePence(r)),
          says: `A ${label(r).toLowerCase()} on ${dayWords(whenOf(r))} can't be read: ${why}. Squeegee counts ${pounds(squeegeePence(r))}. Count it as Squeegee does, leave it out, or type the amount?`,
          options: ['as_squeegee', 'leave_out'], typed: true, default: null, records: [rid],
        })
      }
      // Still being collected by direct debit: counted by Squeegee already, settled once later.
      if (collecting && squeegeePence(r) < 0 && !paired.has(rid)) {
        mandatory.add(rid)
        const pd = isObj(r.paymentDetails) ? r.paymentDetails : {}
        const sub = text(r.transactionSubType)
        account.collections.push({
          ext_id: rid,
          provider: text(pd.paymentProvider) || (sub === 'auto.go-cardless' ? 'gocardless' : sub.replace(/^auto\./, '') || 'other'),
          provider_payment_id: text(pd.paymentId) || (isObj(r.externalIds) ? text(r.externalIds.gocardless) : '') || null,
          amount_pence: -squeegeePence(r),
          on_date: whenOf(r),
          squeegee_status: text(r.status),
        })
      }
    }

    // Two houses (5).
    if (houses.length > 1 && (records.length || account.collections.length)) {
      ask({
        id: `two_houses:${id}`, kind: 'two_houses', number: 5, pence: Math.abs(account.squeegee_pence),
        says: `Has work at ${houses.length} houses in Squeegee, so comes in as ${houses.length} customers. Which house holds the money (${figureWords(account.squeegee_pence)})? ${houses.map((h, i) => `${i + 1}: ${h}`).join('; ')}`,
        options: houses.map((_, i) => `house:${i}`), typed: false, default: 'house:0', records: [],
      })
    }
    // Stopped or archived, with money (1).
    if (account.stopped && (account.squeegee_pence !== 0 || account.questions.length || account.collections.length)) {
      ask({
        id: `stopped:${id}`, kind: 'stopped', number: 1, pence: Math.abs(account.squeegee_pence),
        says: `Is ${c._archived === true ? 'archived' : 'stopped'} in Squeegee and ${owesWords(account.squeegee_pence)}. Bring them in as Lapsed with the money and nothing booked, or leave them out?`,
        options: ['lapsed', 'leave_out'], typed: false, default: 'lapsed', records: [],
      })
    }
    // Already brought over (6). Asked when Squeegee's figure moved, or when the answers above may move it: the
    // difference is worked out from the figure after those answers (settleMoney), and asked about only then.
    // The exact Squeegee id first; a reference alone only when the account it names has no id or the same one. A
    // reference Roundly already holds for a different Squeegee customer is a conflict: nothing comes in for it.
    const knownById = known.find((k) => !!k.squeegee_id && k.squeegee_id === id)
    const knownByRef = known.find((k) => k.squeegee_ref === ref)
    const was = knownById ?? (knownByRef && (!knownByRef.squeegee_id || knownByRef.squeegee_id === id) ? knownByRef : undefined)
    if (!was && knownByRef) account.conflict = true
    if (was) {
      account.already = { accepted_pence: was.accepted_pence, squeegee_ref: was.squeegee_ref }
      if (was.accepted_pence !== account.squeegee_pence || account.questions.length) {
        ask({
          id: `newer:${id}`, kind: 'newer', number: 6, pence: Math.abs(account.squeegee_pence - was.accepted_pence),
          says: `Already brought over: Roundly brought ${figureWords(was.accepted_pence)}, and Squeegee now says ${figureWords(account.squeegee_pence)}. Accept the difference this backup makes, with any answers above?`,
          options: ['accept', 'keep'], typed: false, default: null, records: [],
        })
      }
    }

    // History: lines behind a question or a collection always; all notes up to 200; money from the last 12
    // months up to 100; newest first.
    const money: MoneyLine[] = records.map((r) => ({
      ext_id: text(r._id),
      kind: lineKind(r),
      on_date: whenOf(r),
      text: [label(r), text(r.description).replace(/^(CANCELLED|REVERSAL):\s*/, ''), isObj(r.invoice) && num(r.invoice.invoiceNumber) !== null ? `invoice ${r.invoice.invoiceNumber}` : '']
        .filter((x, i, xs) => x && xs.indexOf(x) === i).join(', ').slice(0, 300),
      amount_pence: squeegeePence(r),
      mandatory: mandatory.has(text(r._id)),
    }))
    const noteLines: MoneyLine[] = (notes.get(id) ?? []).map((n) => ({
      ext_id: text(n._id), kind: 'note' as const, on_date: dateOf({ date: n.createdDate }), text: (typeof n.content === 'string' ? n.content.trim() : '').slice(0, 2000), amount_pence: null, mandatory: false,
    })).filter((n) => n.ext_id && n.text)
    const newest = (a: MoneyLine, b: MoneyLine): number => b.on_date.localeCompare(a.on_date) || a.ext_id.localeCompare(b.ext_id)
    const keptMoney = money.filter((l) => l.mandatory)
    const ordinary = money.filter((l) => !l.mandatory && (!yearBack || l.on_date >= yearBack)).sort(newest)
    keptMoney.push(...ordinary.slice(0, Math.max(0, MONEY_LINES_KEPT - keptMoney.length)))
    const keptNotes = noteLines.sort(newest).slice(0, NOTES_KEPT)
    account.lines = [...keptMoney, ...keptNotes].sort(newest)
    account.lines_left = money.length + noteLines.length - account.lines.length
    accounts.push(account)
  }

  const live = accounts.map((a) => a.squeegee_pence)
  return {
    as_of: asOf,
    currency,
    accounts,
    questions,
    not_brought: notBrought,
    squeegee: {
      owed_pence: live.filter((p) => p > 0).reduce((s, p) => s + p, 0),
      owed_customers: live.filter((p) => p > 0).length,
      credit_pence: live.filter((p) => p < 0).reduce((s, p) => s - p, 0),
      credit_customers: live.filter((p) => p < 0).length,
    },
  }
}

// ---- the answers ----------------------------------------------------------------------------------------------
export interface SettledAccount {
  squeegee_ref: string
  squeegee_id: string
  name: string
  // Which of the customer's houses (Roundly customers) the money sits on.
  house: number
  lapsed: boolean
  opening_pence: number
  squeegee_pence: number
  collections: (MoneyCollection & { opening_effect_pence: number })[]
  history_expected: number
}

export interface Settled {
  // Every question answered (a pick to start with counts), and nothing over the limits.
  ready: boolean
  unanswered: string[]
  // More than COLLECTIONS_MAX direct debits still collecting for one customer: "send us the file".
  too_many: string[]
  // Direct debits whose answers leave them as 0043 cannot hold them (a typed amount that is not a payment):
  // `${ref}:${Squeegee id}`. The owner answers again.
  invalid: string[]
  accounts: SettledAccount[]
  // Already on the round: the difference accepted (never a second opening), with the figure Roundly had when the
  // owner checked (baseline) and the new one (target), so the server refuses a review another import overtook. Named
  // by the reference Roundly holds. An unchanged figure whose direct debits Squeegee has since resolved is here too,
  // with baseline and target the same: the debits are settled, nothing else moves (short check finding 3).
  corrections: { squeegee_ref: string; squeegee_id: string; correction_pence: number; baseline_pence: number; target_pence: number; collections: SettledAccount['collections']; debits: MoneyAccount['debits'] }[]
  // Their reference is Roundly's for a different Squeegee customer: not brought.
  conflicts: { squeegee_ref: string; squeegee_id: string; name: string; squeegee_pence: number }[]
  // Not brought, with Squeegee's figure: stopped ones left out, and stopped ones with nothing owed.
  left_out: { squeegee_ref: string; squeegee_id: string; squeegee_pence: number }[]
  totals: { owed_pence: number; owed_customers: number; credit_pence: number; credit_customers: number; collecting_pence: number; collecting_customers: number }
}

export function settleMoney(m: SqueegeeMoney, answers: Record<string, string> = {}): Settled {
  const byId = new Map(m.questions.map((q) => [q.id, q]))
  const unanswered: string[] = []
  const answer = (qid: string): string | null => {
    const q = byId.get(qid)!
    const a = answers[qid] ?? q.default
    const ok = a !== null && (q.options.includes(a) || (q.typed && /^pence:-?\d{1,9}$/.test(a)))
    if (!ok) unanswered.push(qid)
    return ok ? a : null
  }
  const out: Settled = { ready: false, unanswered, too_many: [], invalid: [], accounts: [], corrections: [], conflicts: [], left_out: [], totals: { owed_pence: 0, owed_customers: 0, credit_pence: 0, credit_customers: 0, collecting_pence: 0, collecting_customers: 0 } }
  for (const a of m.accounts) {
    // Each record's value in pounds as Squeegee holds it (a typed answer is whole pence), and whether it counts.
    const value = new Map(Object.entries(a.records))
    const typed = new Map<string, number>()
    const count = new Map([...value.keys()].map((k) => [k, 1 as 0 | 1]))
    let house = 0
    let lapsed = false
    let leftOut = false
    let newer: string | null = null
    for (const qid of a.questions) {
      const q = byId.get(qid)!
      if (q.kind === 'newer') {
        newer = qid
        continue
      }
      const got = answer(qid)
      if (got === null) continue
      if (q.counts) for (const [rid, c] of Object.entries(q.counts[got] ?? {})) count.set(rid, c)
      else if (q.kind === 'unreadable') {
        const rid = q.records[0]
        if (got.startsWith('pence:')) typed.set(rid, Number(got.slice(6)))
        value.set(rid, got === 'leave_out' ? 0 : got.startsWith('pence:') ? Number(got.slice(6)) / 100 : a.records[rid])
      } else if (q.kind === 'two_houses') house = Number(got.slice(6))
      else if (q.kind === 'stopped') {
        lapsed = got === 'lapsed'
        leftOut = got === 'leave_out'
      }
    }
    // Rounded once, at the end, as Squeegee rounds a customer's balance (two £10.005 records are £20.01).
    const sum = [...value.entries()].reduce((t, [rid, v]) => t + v * (count.get(rid) ?? 1), 0)
    const opening = Math.round((sum ? Math.round(100 * (sum + Number.EPSILON)) / 100 : 0) * 100)
    if (a.conflict) {
      out.conflicts.push({ squeegee_ref: a.squeegee_ref, squeegee_id: a.squeegee_id, name: a.name, squeegee_pence: a.squeegee_pence })
      continue
    }
    // What each direct debit took off the opening, under the answers: its own value when it counts, else 0. A
    // typed amount is what is being collected. Only what 0043 holds is ready: a payment (more than 0) whose
    // effect is minus that, or nothing.
    const collectionsNow = (): SettledAccount['collections'] => a.collections.map((c) => {
      const amount = typed.has(c.ext_id) ? -typed.get(c.ext_id)! : c.amount_pence
      const v = Math.round((value.get(c.ext_id) ?? 0) * 100)
      const effect = (count.get(c.ext_id) ?? 1) === 1 && v !== 0 ? v : 0
      if (!Number.isInteger(amount) || amount <= 0 || (effect !== 0 && effect !== -amount)) out.invalid.push(`${a.squeegee_ref}:${c.ext_id}`)
      return { ...c, amount_pence: amount, opening_effect_pence: effect }
    })
    if (a.already) {
      // The difference from what Roundly already took, worked out after every answer above, with the debits this
      // backup still has collecting: one Roundly doesn't hold yet comes in as "Collecting, don't chase" (short check).
      const difference = opening - a.already.accepted_pence
      const collections = collectionsNow()
      // Each debit as the owner's answers leave it: one Squeegee marks failed but the owner says was paid is collected.
      const debits = a.debits.map((d) => ({ ...d, state: d.state === 'failed' && (count.get(d.ext_id) ?? 1) === 1 ? 'collected' as const : d.state }))
      const correction = { squeegee_ref: a.already.squeegee_ref, squeegee_id: a.squeegee_id, correction_pence: difference, baseline_pence: a.already.accepted_pence, target_pence: opening, collections, debits }
      if (difference !== 0) {
        const got = newer === null ? null : answer(newer)
        if (newer === null) unanswered.push(`newer:${a.squeegee_id}`)
        if (got === 'accept') out.corrections.push(correction)
      } else if (debits.some((d) => d.state !== 'collecting') || collections.length) {
        out.corrections.push(correction)
      }
      continue
    }
    if (leftOut || (a.stopped && !lapsed)) {
      out.left_out.push({ squeegee_ref: a.squeegee_ref, squeegee_id: a.squeegee_id, squeegee_pence: a.squeegee_pence })
      continue
    }
    if (a.collections.length > COLLECTIONS_MAX) out.too_many.push(a.squeegee_ref)
    const collections = collectionsNow()
    out.accounts.push({ squeegee_ref: a.squeegee_ref, squeegee_id: a.squeegee_id, name: a.name, house, lapsed, opening_pence: opening, squeegee_pence: a.squeegee_pence, collections, history_expected: a.lines.length })
    if (opening > 0) { out.totals.owed_pence += opening; out.totals.owed_customers++ }
    if (opening < 0) { out.totals.credit_pence -= opening; out.totals.credit_customers++ }
    if (collections.length) { out.totals.collecting_pence += collections.reduce((s, c) => s + c.amount_pence, 0); out.totals.collecting_customers++ }
  }
  out.ready = unanswered.length === 0 && out.too_many.length === 0 && out.invalid.length === 0
  return out
}

// The backup's day in the round's time zone: the switch date. Money from a backup made before today is
// refused (PLAN section 4, Liam Q11).
export function moneyDay(asOf: string, timeZone: string): string {
  return dayIn(timeZone, new Date(asOf))
}
